/**
 * Employer Shortlist — /employer/shortlist
 *
 * A dedicated dashboard for candidates the current employer has bookmarked
 * from the Candidates Store or public résumé pages. Each row shows:
 *   · Current snapshot of the candidate (headline, location, superpower,
 *     salary, availability, remote_mode, skills, cert badge, featured)
 *   · Delta badges for what's changed since the employer saved them
 *     (e.g. "Now Actively Looking!", "Salary → $95K–$180K", "+3 new skills")
 *   · Inline editable notes + tag chip (dropdown: interview/backup/hot/reject/review)
 *   · Actions: view résumé, send introduction, unsave
 *
 * Verified employers only (backend returns 403 for pending).
 */
import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, ArrowRight, Loader, Star, StarOff, MapPin, Zap,
  DollarSign, Home, Mail, Trash2, Save, TrendingUp, AlertTriangle,
  Sparkles, Store, Search, Briefcase, LogOut,
} from "lucide-react";
import { useEmployer } from "../components/employer/EmployerProtected";
import ImpersonationBanner from "../components/employer/ImpersonationBanner";
import {
  NAVY, ORANGE, MUSTARD, CREAM,
  MIDNIGHT_BG_STYLE, GLASS_HEADER_STYLE, CARD_DEEP_SHADOW,
} from "../components/employer/employerTheme";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TAG_OPTIONS = [
  { value: "",          label: "No tag" },
  { value: "interview", label: "Interview" },
  { value: "hot",       label: "Hot" },
  { value: "backup",    label: "Backup" },
  { value: "review",    label: "Review" },
  { value: "reject",    label: "Reject" },
];
const TAG_COLORS = {
  interview: { bg: "#DBEAFE", color: "#1E3A8A", border: "#3B82F6" },
  hot:       { bg: "#FEE2E2", color: "#991B1B", border: "#EF4444" },
  backup:    { bg: "#F1F5F9", color: "#334155", border: "#94A3B8" },
  review:    { bg: "#FEF3C7", color: "#78350F", border: "#F59E0B" },
  reject:    { bg: "#F5F5F4", color: "#57534E", border: "#78716C" },
};

function TagChip({ tag }) {
  if (!tag) return null;
  const s = TAG_COLORS[tag] || TAG_COLORS.backup;
  return (
    <span
      className="inline-flex items-center gap-1 text-[9px] font-mono-rm uppercase tracking-widest px-1.5 py-0.5 rounded"
      style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}`, fontWeight: 800 }}
    >
      · {tag}
    </span>
  );
}

function DeltaBadges({ deltas }) {
  if (!deltas?.length) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-2" data-testid="shortlist-deltas">
      {deltas.map((d, i) => {
        const isHot = d.kind === "hot";
        const isWarm = d.kind === "warm";
        const bg  = isHot ? "#DCFCE7" : isWarm ? CREAM : "#EFF6FF";
        const color = isHot ? "#065F46" : isWarm ? "#7C2D12" : "#1E3A8A";
        const border = isHot ? "#10B981" : isWarm ? ORANGE : "#3B82F6";
        const label = (() => {
          if (d.field === "availability") return `Now: ${(d.to || "").replace(/_/g, " ")}`;
          if (d.field === "remote_mode")  return `Now open to: ${d.to}`;
          if (d.field === "salary")       return `Salary → ${d.to}`;
          if (d.field === "skills")       return `+ Skills: ${d.to}`;
          if (d.field === "headline")     return `New headline`;
          if (d.field === "superpower")   return `Refreshed superpower`;
          return `${d.field}: ${d.to}`;
        })();
        return (
          <span
            key={i}
            className="inline-flex items-center gap-1 text-[10px] font-mono-rm uppercase tracking-widest px-1.5 py-0.5 rounded"
            style={{ background: bg, color, border: `1px solid ${border}`, fontWeight: 700 }}
          >
            <TrendingUp size={9} /> {label}
          </span>
        );
      })}
    </div>
  );
}

export default function EmployerShortlist() {
  const navigate = useNavigate();
  const { employer } = useEmployer();
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ total: 0, by_tag: {}, has_deltas: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tagFilter, setTagFilter] = useState("all"); // "all" | "interview" | ...
  // Editing state per-row for tag + notes
  const [editing, setEditing] = useState({}); // { [id]: { tag, notes, saving } }

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const r = await axios.get(`${BACKEND_URL}/api/employer/shortlist`,
        { withCredentials: true });
      setRows(r.data.shortlist || []);
      setCounts(r.data.counts || { total: 0, by_tag: {}, has_deltas: 0 });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to load shortlist.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const logout = async () => {
    const isAdminShadow = employer?.is_admin_shadow;
    try {
      if (isAdminShadow) {
        await axios.post(`${BACKEND_URL}/api/logout`, {}, { withCredentials: true });
      } else {
        await axios.post(`${BACKEND_URL}/api/employer/logout`, {}, { withCredentials: true });
      }
    } catch { /* ignore */ }
    navigate(isAdminShadow ? "/login" : "/employer/login", { replace: true });
  };

  const startEdit = (row) => {
    setEditing((prev) => ({
      ...prev,
      [row.id]: { tag: row.tag || "", notes: row.notes || "", saving: false },
    }));
  };
  const cancelEdit = (rowId) => {
    setEditing((prev) => {
      const next = { ...prev };
      delete next[rowId];
      return next;
    });
  };
  const saveEdit = async (row) => {
    const patch = editing[row.id];
    if (!patch) return;
    setEditing((prev) => ({ ...prev, [row.id]: { ...patch, saving: true } }));
    try {
      await axios.patch(
        `${BACKEND_URL}/api/employer/shortlist/${row.publication_public_id}`,
        { tag: patch.tag || null, notes: patch.notes || "" },
        { withCredentials: true },
      );
      // Update local row optimistically
      setRows((prev) => prev.map((r) =>
        r.id === row.id ? { ...r, tag: patch.tag || null, notes: patch.notes || "" } : r,
      ));
      cancelEdit(row.id);
    } catch (err) {
      setEditing((prev) => ({ ...prev, [row.id]: { ...patch, saving: false } }));
      setError(err?.response?.data?.detail || "Save failed.");
    }
  };
  const remove = async (row) => {
    if (!window.confirm(`Remove ${row.candidate_display || "this candidate"} from your shortlist?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/employer/shortlist/${row.publication_public_id}`,
        { withCredentials: true });
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      setCounts((prev) => ({ ...prev, total: Math.max(0, (prev.total || 1) - 1) }));
    } catch (err) {
      setError(err?.response?.data?.detail || "Remove failed.");
    }
  };

  const filtered = tagFilter === "all"
    ? rows
    : tagFilter === "untagged"
      ? rows.filter((r) => !r.tag)
      : rows.filter((r) => r.tag === tagFilter);

  return (
    <div className="min-h-screen" style={MIDNIGHT_BG_STYLE}>
      <ImpersonationBanner
        employer={employer}
        onExit={() => { window.location.href = "/admin/employers"; }}
      />
      {/* Header — Resmo wordmark + glassy band */}
      <div className="border-b backdrop-blur-md" style={GLASS_HEADER_STYLE} data-testid="shortlist-header">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-4 min-w-0">
            <Link to="/discover" style={{ textDecoration: "none" }} className="flex items-center shrink-0" data-testid="shortlist-brand">
              <span
                className="font-display uppercase leading-none"
                style={{ fontSize: "1.6rem", color: "white", letterSpacing: "-0.02em", fontWeight: 900 }}
              >
                <span style={{ color: ORANGE }}>R</span>esmo
              </span>
              <span
                className="ml-2 hidden md:inline-block font-mono-rm uppercase tracking-widest px-1.5 py-0.5 rounded"
                style={{ fontSize: "9px", color: NAVY, background: MUSTARD, fontWeight: 800, border: `1px solid ${MUSTARD}` }}
              >
                · Talent Directory
              </span>
            </Link>
            <span className="hidden md:inline-block" style={{ color: "rgba(255,255,255,0.28)" }}>•</span>
            <div className="min-w-0">
              <div className="font-mono-rm text-[10px] uppercase tracking-widest" style={{ color: MUSTARD, fontWeight: 700 }}>
                · Shortlist
              </div>
              <div className="font-display uppercase leading-tight truncate" style={{ fontSize: "1.15rem", color: "white", letterSpacing: "-0.01em" }}>
                Your bookmarked candidates.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Link to="/employer/store"
                  className="inline-flex items-center gap-1 rounded px-2 py-1 text-[10px] font-mono-rm uppercase tracking-widest"
                  style={{ color: NAVY, background: MUSTARD, border: `1px solid ${MUSTARD}`, fontWeight: 800, textDecoration: "none" }}
                  data-testid="shortlist-nav-store">
              <Store size={11} /> Candidates Store
            </Link>
            <Link to="/discover"
                  className="hidden md:inline-flex items-center gap-1 rounded px-2 py-1 text-[10px] font-mono-rm uppercase tracking-widest"
                  style={{ color: MUSTARD, border: `1px solid ${MUSTARD}`, fontWeight: 700, textDecoration: "none" }}
                  data-testid="shortlist-nav-discover">
              <Search size={11} /> Quick search
            </Link>
            <Link to="/employer/requests"
                  className="hidden md:inline-flex items-center gap-1 rounded px-2 py-1 text-[10px] font-mono-rm uppercase tracking-widest"
                  style={{ color: MUSTARD, border: `1px solid ${MUSTARD}`, fontWeight: 700, textDecoration: "none" }}
                  data-testid="shortlist-nav-outbox">
              <Briefcase size={11} /> My requests
            </Link>
            <span className="hidden md:inline-block text-[11px]" style={{ color: "#94A3B8" }}>
              {employer?.company}
            </span>
            <button onClick={logout}
                    className="inline-flex items-center gap-1 rounded px-2 py-1 text-[10px] font-mono-rm uppercase tracking-widest"
                    style={{ background: "transparent", color: MUSTARD, border: `1px solid ${MUSTARD}`, fontWeight: 700 }}
                    data-testid="shortlist-logout">
              <LogOut size={11} /> Sign out
            </button>
          </div>
        </div>
      </div>

      {/* Summary + tag filter strip */}
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-4">
        <div className="rounded-md p-3 flex items-center flex-wrap gap-3"
             style={{ background: "white", border: "1.5px solid #CBD5E1", boxShadow: CARD_DEEP_SHADOW }}
             data-testid="shortlist-summary">
          <div className="font-mono-rm text-[10px] uppercase tracking-widest" style={{ color: NAVY, fontWeight: 800 }}>
            · {counts.total || 0} saved
          </div>
          {counts.has_deltas > 0 && (
            <div className="font-mono-rm text-[10px] uppercase tracking-widest inline-flex items-center gap-1"
                 style={{ color: ORANGE, fontWeight: 800 }}
                 data-testid="shortlist-deltas-summary">
              <TrendingUp size={10} /> · {counts.has_deltas} with updates since last visit
            </div>
          )}
          <div className="flex-1" />
          {/* Tag filter chips */}
          <div className="flex items-center gap-1 flex-wrap" data-testid="shortlist-tag-filters">
            {["all", ...Object.keys(counts.by_tag || {})].map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTagFilter(t)}
                className="text-[10px] font-mono-rm uppercase tracking-widest px-2 py-1 rounded"
                style={{
                  background: tagFilter === t ? NAVY : "white",
                  color: tagFilter === t ? MUSTARD : NAVY,
                  border: `1px solid ${NAVY}`,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
                data-testid={`shortlist-tag-${t}`}
              >
                {t === "all" ? `All (${counts.total || 0})` : `${t} (${(counts.by_tag || {})[t] || 0})`}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Errors */}
      {error && (
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="rounded-md p-3 text-[12px]"
               style={{ background: "#FEE2E2", border: "1px solid #FCA5A5", color: "#7F1D1D" }}
               data-testid="shortlist-error">
            {error}
          </div>
        </div>
      )}

      {/* Rows */}
      <div className="max-w-6xl mx-auto px-4 md:px-8 pb-10">
        {loading && (
          <div className="flex items-center justify-center py-16 gap-2" data-testid="shortlist-loading">
            <Loader size={16} className="animate-spin" style={{ color: MUSTARD }} />
            <span className="font-mono-rm text-[10px] uppercase tracking-widest" style={{ color: MUSTARD }}>
              · Loading your shortlist…
            </span>
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="rounded-md py-16 text-center"
               style={{
                 background: "rgba(255,255,255,0.06)",
                 border: `1px dashed ${MUSTARD}`,
                 backdropFilter: "blur(6px)",
               }}
               data-testid="shortlist-empty">
            <StarOff size={22} style={{ color: MUSTARD }} className="mx-auto mb-2" />
            <div className="font-mono-rm text-[10px] uppercase tracking-widest mb-1" style={{ color: MUSTARD, fontWeight: 700 }}>
              · Your shortlist is empty
            </div>
            <div className="text-[13px] mb-4" style={{ color: "rgba(255,255,255,0.78)" }}>
              Save candidates from the Store with the ⭐ button — they&apos;ll appear here with change alerts.
            </div>
            <Link to="/employer/store"
                  className="inline-flex items-center gap-1 rounded-md px-4 py-2 font-mono-rm uppercase text-[11px] tracking-widest"
                  style={{ background: ORANGE, color: "white", border: `2px solid ${NAVY}`, boxShadow: `3px 3px 0 0 ${NAVY}`, fontWeight: 700, textDecoration: "none" }}
                  data-testid="shortlist-empty-cta">
              <Store size={12} /> Open Candidates Store <ArrowRight size={11} />
            </Link>
          </div>
        )}
        <div className="space-y-3">
          {filtered.map((row) => (
            <ShortlistRow
              key={row.id}
              row={row}
              editing={editing[row.id]}
              onStartEdit={() => startEdit(row)}
              onCancelEdit={() => cancelEdit(row.id)}
              onSaveEdit={() => saveEdit(row)}
              onChangeEdit={(patch) => setEditing((prev) => ({
                ...prev, [row.id]: { ...prev[row.id], ...patch },
              }))}
              onRemove={() => remove(row)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function ShortlistRow({ row, editing, onStartEdit, onCancelEdit, onSaveEdit, onChangeEdit, onRemove }) {
  const c = row.current || {};
  const isMissing = row.status === "missing" || !c;
  const isUnpublished = c && c.status === "unpublished";
  return (
    <div
      className="rounded-md p-4"
      style={{
        background: "white",
        border: `1.5px solid ${row.deltas?.length ? ORANGE : "#CBD5E1"}`,
        boxShadow: row.deltas?.length
          ? `4px 4px 0 0 ${MUSTARD}, 0 12px 36px rgba(0,0,0,0.4)`
          : "3px 3px 0 0 rgba(255,193,69,0.55), 0 8px 24px rgba(0,0,0,0.3)",
        opacity: isMissing || isUnpublished ? 0.75 : 1,
      }}
      data-testid={`shortlist-row-${row.publication_public_id}`}
    >
      <div className="flex items-start gap-3">
        {/* Avatar */}
        <div style={{ flex: "0 0 44px" }}>
          {c.superpower_avatar_url ? (
            <img
              src={c.superpower_avatar_url}
              alt="candidate"
              style={{ width: 44, height: 44, borderRadius: 6, border: `1.5px solid ${NAVY}`, objectFit: "cover" }}
            />
          ) : (
            <span
              className="inline-flex items-center justify-center rounded"
              style={{ width: 44, height: 44, background: NAVY, color: MUSTARD, border: `1.5px solid ${NAVY}` }}
            >
              <Zap size={20} />
            </span>
          )}
        </div>

        {/* Meat */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-display uppercase leading-tight" style={{ fontSize: "1.05rem", color: NAVY, letterSpacing: "-0.01em" }}>
              {row.candidate_display}
            </div>
            <TagChip tag={row.tag} />
            {c.featured && (
              <span
                className="inline-flex items-center gap-1 text-[9px] font-mono-rm uppercase tracking-widest px-1.5 py-0.5 rounded"
                style={{ background: MUSTARD, color: NAVY, fontWeight: 800, border: `1px solid ${NAVY}` }}
              >
                <Star size={9} fill={NAVY} /> Featured
              </span>
            )}
            {isMissing && (
              <span
                className="inline-flex items-center gap-1 text-[9px] font-mono-rm uppercase tracking-widest px-1.5 py-0.5 rounded"
                style={{ background: "#FEE2E2", color: "#7F1D1D", fontWeight: 800, border: "1px solid #FCA5A5" }}
              >
                <AlertTriangle size={9} /> Publication deleted
              </span>
            )}
            {isUnpublished && (
              <span
                className="inline-flex items-center gap-1 text-[9px] font-mono-rm uppercase tracking-widest px-1.5 py-0.5 rounded"
                style={{ background: "#F1F5F9", color: "#475569", fontWeight: 800, border: "1px solid #CBD5E1" }}
              >
                Unpublished
              </span>
            )}
          </div>
          {c.headline && <div className="text-[12px]" style={{ color: "#334155" }}>{c.headline}</div>}

          {c.superpower && (
            <div className="text-[12px] mt-1" style={{ color: ORANGE, fontWeight: 600 }}>
              ⚡ {c.superpower}
            </div>
          )}

          <div className="flex items-center flex-wrap gap-2 mt-1">
            {c.location && (
              <span className="inline-flex items-center gap-1 text-[11px]" style={{ color: "#64748B" }}>
                <MapPin size={11} /> {c.location}
              </span>
            )}
            {c.years_experience > 0 && (
              <span className="text-[11px]" style={{ color: "#64748B" }}>{c.years_experience} yrs</span>
            )}
            {(c.salary_min || c.salary_max) && (
              <span className="inline-flex items-center gap-1 text-[11px]" style={{ color: "#475569" }}>
                <DollarSign size={10} />
                {c.salary_min && c.salary_max
                  ? `$${(c.salary_min || 0).toLocaleString()}–$${(c.salary_max || 0).toLocaleString()}`
                  : (c.salary_min ? `$${c.salary_min.toLocaleString()}+` : `up to $${c.salary_max.toLocaleString()}`)}
              </span>
            )}
            {c.remote_mode && c.remote_mode !== "any" && (
              <span className="inline-flex items-center gap-1 text-[10px] font-mono-rm uppercase tracking-widest px-1.5 py-0.5 rounded"
                    style={{ background: "#EFF6FF", color: "#1E40AF", border: "1px solid #BFDBFE", fontWeight: 700 }}>
                <Home size={9} /> {c.remote_mode}
              </span>
            )}
            {c.availability && (
              <span className="inline-flex items-center gap-1 text-[10px] font-mono-rm uppercase tracking-widest px-1.5 py-0.5 rounded"
                    style={{ background: "#DCFCE7", color: "#065F46", border: "1px solid #86EFAC", fontWeight: 700 }}>
                {c.availability.replace(/_/g, " ")}
              </span>
            )}
          </div>

          {c.skills?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {c.skills.slice(0, 8).map((s, i) => (
                <span key={i}
                      className="text-[10px] px-1.5 py-0.5 rounded"
                      style={{ background: "#F1F5F9", color: "#334155", border: "1px solid #E2E8F0" }}>
                  {s}
                </span>
              ))}
            </div>
          )}

          <DeltaBadges deltas={row.deltas} />

          {/* Notes + tag editor */}
          {editing ? (
            <div className="mt-3 rounded p-3"
                 style={{ background: CREAM, border: `1.5px dashed ${ORANGE}` }}
                 data-testid={`shortlist-edit-${row.publication_public_id}`}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <label className="block md:col-span-2">
                  <div className="font-mono-rm text-[9px] uppercase tracking-widest mb-1" style={{ color: NAVY, fontWeight: 700 }}>
                    · Notes
                  </div>
                  <textarea
                    value={editing.notes}
                    onChange={(e) => onChangeEdit({ notes: e.target.value })}
                    rows={2}
                    maxLength={600}
                    placeholder="Why did you save them? Interview slot? Salary target?"
                    className="w-full rounded p-2 text-[12px]"
                    style={{ border: "1.5px solid #CBD5E1", background: "white" }}
                    data-testid={`shortlist-edit-notes-${row.publication_public_id}`}
                  />
                </label>
                <label className="block">
                  <div className="font-mono-rm text-[9px] uppercase tracking-widest mb-1" style={{ color: NAVY, fontWeight: 700 }}>
                    · Tag
                  </div>
                  <select
                    value={editing.tag}
                    onChange={(e) => onChangeEdit({ tag: e.target.value })}
                    className="w-full rounded px-2 py-1.5 text-[12px]"
                    style={{ border: "1.5px solid #CBD5E1", background: "white" }}
                    data-testid={`shortlist-edit-tag-${row.publication_public_id}`}
                  >
                    {TAG_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="flex items-center justify-end gap-2 mt-2">
                <button
                  type="button"
                  onClick={onCancelEdit}
                  className="text-[10px] font-mono-rm uppercase tracking-widest px-2 py-1 rounded"
                  style={{ background: "white", color: NAVY, border: `1.5px solid ${NAVY}`, fontWeight: 700 }}
                  data-testid={`shortlist-edit-cancel-${row.publication_public_id}`}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={onSaveEdit}
                  disabled={editing.saving}
                  className="inline-flex items-center gap-1 text-[10px] font-mono-rm uppercase tracking-widest px-3 py-1 rounded"
                  style={{ background: ORANGE, color: "white", border: `1.5px solid ${NAVY}`, fontWeight: 800 }}
                  data-testid={`shortlist-edit-save-${row.publication_public_id}`}
                >
                  {editing.saving ? <Loader size={10} className="animate-spin" /> : <Save size={10} />}
                  Save
                </button>
              </div>
            </div>
          ) : (
            row.notes ? (
              <div
                onClick={onStartEdit}
                className="mt-2 rounded p-2 text-[12px] cursor-pointer"
                style={{ background: "#F8FAFC", border: "1px dashed #CBD5E1", color: "#334155" }}
                data-testid={`shortlist-notes-${row.publication_public_id}`}
              >
                {row.notes}
              </div>
            ) : null
          )}
        </div>

        {/* Action rail */}
        <div className="hidden md:flex flex-col items-end gap-1.5" style={{ flex: "0 0 130px" }}>
          {!isMissing && (
            <Link
              to={`/r/${row.publication_public_id}`}
              className="inline-flex items-center justify-center gap-1 rounded px-2 py-1 text-[10px] font-mono-rm uppercase tracking-widest w-full"
              style={{ background: NAVY, color: MUSTARD, fontWeight: 700, textDecoration: "none", border: `1.5px solid ${NAVY}` }}
              data-testid={`shortlist-view-${row.publication_public_id}`}
            >
              <ArrowRight size={10} /> View résumé
            </Link>
          )}
          {!editing && (
            <button
              type="button"
              onClick={onStartEdit}
              className="inline-flex items-center justify-center gap-1 rounded px-2 py-1 text-[10px] font-mono-rm uppercase tracking-widest w-full"
              style={{ background: "white", color: NAVY, border: `1.5px solid ${NAVY}`, fontWeight: 700 }}
              data-testid={`shortlist-edit-${row.publication_public_id}`}
            >
              <Save size={10} /> Notes + tag
            </button>
          )}
          <button
            type="button"
            onClick={onRemove}
            className="inline-flex items-center justify-center gap-1 rounded px-2 py-1 text-[10px] font-mono-rm uppercase tracking-widest w-full"
            style={{ background: "white", color: "#B91C1C", border: "1.5px solid #FCA5A5", fontWeight: 700 }}
            data-testid={`shortlist-remove-${row.publication_public_id}`}
          >
            <Trash2 size={10} /> Remove
          </button>
        </div>
      </div>
    </div>
  );
}
